import { Injectable, inject, OnDestroy } from '@angular/core';
import { Observable, Subject, BehaviorSubject, Subscription } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { distinctUntilChanged } from 'rxjs/operators';
import { io, Socket } from 'socket.io-client';
import { AuthService } from './auth.service';
import { ApiConfigService } from './api-config.service';
import { ApiAuthService } from './api-auth.service';

@Injectable({ providedIn: 'root' })
export class ApiWebSocketService implements OnDestroy {
  private authService = inject(AuthService);
  private apiAuthService = inject(ApiAuthService);
  private apiConfig = inject(ApiConfigService);
  private socket: Socket | null = null;
  private connected$ = new BehaviorSubject<boolean>(false);
  private retryCount = 0;
  private readonly maxRetries = 10;
  private initializeTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly RESUME_FROM_BACKGROUND_DELAY_MS = 2000;
  private authProfileSub?: Subscription;
  /** Last requested delivery pool room (`zone uuid` or `all`). Survives disconnect so we rejoin on connect. */
  private pendingDeliveryZoneId: string | null = null;
  private joinDeliveryZoneAttempt = 0;
  private joinDeliveryZoneRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly JOIN_DELIVERY_ZONE_MAX_ATTEMPTS = 5;
  private readonly JOIN_DELIVERY_ZONE_ACK_TIMEOUT_MS = 4000;
  static readonly DELIVERY_ZONE_ALL = 'all';

  // Event subjects
  private orderStatusUpdate$ = new Subject<any>();
  private orderETAUpdate$ = new Subject<any>();
  private newOrder$ = new Subject<any>();
  private chatMessage$ = new Subject<any>();
  private notification$ = new Subject<any>();
  private userTyping$ = new Subject<any>();
  private messagesRead$ = new Subject<any>();
  private deliveryAgentUpdate$ = new Subject<any>();
  private deliveryAgentLocation$ = new Subject<any>();
  private productUpdate$ = new Subject<any>();
  private vendorUpdate$ = new Subject<any>();
  private orderSettingsUpdate$ = new Subject<any>();
  // CRITICAL: Reconnect trigger to fetch missed orders
  private reconnectTrigger$ = new Subject<void>();

  constructor() {
    // Delay connection to ensure API config is initialized
    // This is especially important for Android devices where wsUrl needs to be updated
    // APP_INITIALIZER runs before app bootstrap, so we wait a bit longer
    // Try connecting after a delay, and retry if URL is still localhost on mobile
    setTimeout(() => {
      this.attemptConnection();
    }, 1000);

    // Connect when the user logs in — only when identity actually changes (not on every signal refresh).
    this.authProfileSub = toObservable(this.authService.profile)
      .pipe(
        distinctUntilChanged(
          (a, b) =>
            (a?.id ?? '') === (b?.id ?? '') && !!a === !!b,
        ),
      )
      .subscribe((profile) => {
        if (profile && !this.socket?.connected) {
          console.log('[WebSocket] User authenticated, attempting connection...');
          setTimeout(() => {
            this.attemptConnection();
          }, 500);
        } else if (!profile) {
          console.log('[WebSocket] User logged out, disconnecting...');
          this.disconnect();
        }
      });
  }

  private attemptConnection() {
    const wsUrl = this.apiConfig.getWsUrl();

    // Only Capacitor native apps rewrite localhost → device/LAN IP in APP_INITIALIZER.
    // Do not use User-Agent "mobile" here: Chrome DevTools device mode / mobile browsers
    // on desktop localhost would otherwise block WebSocket forever (POS local testing).
    if (typeof window !== 'undefined') {
      const cap = (window as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
      const isNative = !!cap?.isNativePlatform?.();

      if (isNative && wsUrl && wsUrl.includes('localhost')) {
        this.retryCount++;
        if (this.retryCount > this.maxRetries) {
          console.error('[WebSocket] Max retries reached. API config may not be initialized correctly.');
          return;
        }
        console.warn(
          `[WebSocket] Still using localhost on native, retrying in 3s... (${this.retryCount}/${this.maxRetries})`,
        );
        setTimeout(() => {
          this.attemptConnection();
        }, 3000);
        return;
      }
    }

    // Reset retry count on successful connection attempt
    this.retryCount = 0;
    this.connect();
  }

  private connect() {
    // Disconnect existing connection if any
    if (this.socket) {
      this.disconnect();
    }

    // Use ApiAuthService to get the token (uses correct key: 'cartco_jwt_token')
    const token = this.apiAuthService.getToken();
    if (!token) {
      console.warn('[WebSocket] No auth token available, skipping connection');
      console.warn('[WebSocket] User may need to log in first');
      return;
    }

    const wsUrl = this.apiConfig.getWsUrl();
    if (!wsUrl) {
      console.warn('[WebSocket] No WebSocket URL configured, skipping connection');
      console.warn('[WebSocket] Check API configuration');
      return;
    }

    console.log('[WebSocket] Connecting to:', `${wsUrl}/chat`);
    console.log('[WebSocket] Token available:', !!token);

    // WebSocket first: avoids Engine.IO long-polling storm (repeated /socket.io/?transport=polling).
    // Polling remains as fallback on networks that block WebSockets.
    this.socket = io(`${wsUrl}/chat`, {
      auth: { token },
      transports: ['websocket', 'polling'],
      upgrade: true,
      rememberUpgrade: true,
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 15000,
      randomizationFactor: 0.5,
      reconnectionAttempts: 8,
    });

    this.socket.on('connect', () => {
      console.log('[WebSocket] Connected successfully');
      this.connected$.next(true);
      // Rejoin the delivery zone room before reconcile — socket.io rooms are lost on reconnect.
      this.joinDeliveryZoneAttempt = 0;
      this.emitJoinDeliveryZone();
      // CRITICAL: Emit reconnect event so services can fetch missed orders
      this.reconnectTrigger$.next();
    });

    this.socket.on('disconnect', (reason: string) => {
      console.log('[WebSocket] Disconnected:', reason);
      this.connected$.next(false);
    });

    this.socket.on('connect_error', (error: any) => {
      console.error('[WebSocket] Connection error:', error.message || error);
      this.connected$.next(false);
    });

    // Listen for events
    this.socket.on('order-status-update', (data: any) => {
      this.orderStatusUpdate$.next(data);
    });

    this.socket.on('order-eta-update', (data: any) => {
      this.orderETAUpdate$.next(data);
    });

    this.socket.on('new-order', (data: any) => {
      this.newOrder$.next(data);
    });

    this.socket.on('new-message', (data: any) => {
      this.chatMessage$.next(data);
    });

    this.socket.on('chat-notification', (data: any) => {
      this.chatMessage$.next(data);
    });

    this.socket.on('notification', (data: any) => {
      this.notification$.next(data);
    });

    this.socket.on('user-typing', (data: any) => {
      this.userTyping$.next(data);
    });

    this.socket.on('messages-read', (data: any) => {
      this.messagesRead$.next(data);
    });

    this.socket.on('delivery-agent-update', (data: any) => {
      this.deliveryAgentUpdate$.next(data);
    });

    this.socket.on('delivery-agent-location', (data: any) => {
      this.deliveryAgentLocation$.next(data);
    });

    this.socket.on('delivery-agent-status-update', (data: any) => {
      this.deliveryAgentUpdate$.next(data);
    });

    this.socket.on('product-update', (data: any) => {
      this.productUpdate$.next(data);
    });

    this.socket.on('vendor-update', (data: any) => {
      this.vendorUpdate$.next(data);
    });

    this.socket.on('order-settings-update', (data: any) => {
      this.orderSettingsUpdate$.next(data);
    });
  }

  reconnect() {
    console.log('[WebSocket] Reconnecting...');
    this.disconnect();
    // Small delay to ensure disconnect completes
    setTimeout(() => {
      this.connect();
    }, 100);
  }

  /**
   * Initialize connection - can be called manually after API config is ready
   * Useful for ensuring connection happens after APP_INITIALIZER completes
   * Throttled to prevent multiple simultaneous reconnection attempts.
   */
  initialize() {
    if (this.socket?.connected) return;
    if (this.initializeTimeout) return;

    this.initializeTimeout = setTimeout(() => {
      this.initializeTimeout = null;
      if (!this.socket?.connected) {
        console.log('[WebSocket] Initializing connection...');
        this.connect();
      }
    }, 300);
  }

  /**
   * Reconnect when app resumes from background.
   * Uses a delay to give the OS time to release zombie connection resources,
   * avoiding "Insufficient resources" errors on mobile.
   */
  resumeFromBackground(): void {
    if (this.socket?.connected) return;
    console.log('[WebSocket] App resumed, will reconnect in', this.RESUME_FROM_BACKGROUND_DELAY_MS, 'ms');
    setTimeout(() => {
      if (!this.socket?.connected) {
        this.initialize();
      }
    }, this.RESUME_FROM_BACKGROUND_DELAY_MS);
  }

  disconnect() {
    this.clearJoinDeliveryZoneRetry();
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
      this.connected$.next(false);
    }
  }

  /**
   * Delivery app: subscribe to pool-order events for the agent's current service zone.
   * Stores the zone so it is re-joined automatically on every connect/reconnect.
   * If the socket is not connected yet, the join is queued until `connect`.
   */
  joinDeliveryZone(serviceZoneId: string) {
    const zoneId = serviceZoneId?.trim();
    if (!zoneId) return;
    this.pendingDeliveryZoneId = zoneId;
    this.joinDeliveryZoneAttempt = 0;
    this.emitJoinDeliveryZone();
  }

  /** Dev all-zones: subscribe to the shared delivery-pool room (no GPS zone filter). */
  joinAllDeliveryZones() {
    this.joinDeliveryZone(ApiWebSocketService.DELIVERY_ZONE_ALL);
  }

  /** Stop receiving pool-order events (agent outside service zone or list suppressed). */
  leaveDeliveryZone() {
    this.pendingDeliveryZoneId = null;
    this.joinDeliveryZoneAttempt = 0;
    this.clearJoinDeliveryZoneRetry();
    if (this.socket?.connected) {
      this.socket.emit('leave-delivery-zone');
    }
  }

  private emitJoinDeliveryZone() {
    this.clearJoinDeliveryZoneRetry();
    const zoneId = this.pendingDeliveryZoneId;
    if (!zoneId || !this.socket?.connected) {
      return;
    }

    this.joinDeliveryZoneAttempt += 1;
    let settled = false;
    const finish = (ok: boolean) => {
      if (settled) return;
      settled = true;
      if (ok) {
        this.joinDeliveryZoneAttempt = 0;
        return;
      }
      this.scheduleJoinDeliveryZoneRetry();
    };

    const ackTimer = setTimeout(() => finish(false), this.JOIN_DELIVERY_ZONE_ACK_TIMEOUT_MS);
    this.socket.emit('join-delivery-zone', zoneId, (ack?: { success?: boolean; room?: string; message?: string }) => {
      clearTimeout(ackTimer);
      if (ack?.success) {
        console.log('[WebSocket] Joined delivery zone room', ack.room ?? zoneId);
        finish(true);
        return;
      }
      console.warn('[WebSocket] join-delivery-zone failed', ack?.message ?? 'no ack');
      finish(false);
    });
  }

  private scheduleJoinDeliveryZoneRetry() {
    if (!this.pendingDeliveryZoneId) return;
    if (this.joinDeliveryZoneAttempt >= this.JOIN_DELIVERY_ZONE_MAX_ATTEMPTS) {
      console.warn(
        `[WebSocket] Failed to join delivery zone after ${this.JOIN_DELIVERY_ZONE_MAX_ATTEMPTS} attempts`,
      );
      return;
    }
    if (!this.socket?.connected) return;
    const delay = Math.min(1000 * this.joinDeliveryZoneAttempt, 5000);
    this.joinDeliveryZoneRetryTimer = setTimeout(() => this.emitJoinDeliveryZone(), delay);
  }

  private clearJoinDeliveryZoneRetry() {
    if (this.joinDeliveryZoneRetryTimer) {
      clearTimeout(this.joinDeliveryZoneRetryTimer);
      this.joinDeliveryZoneRetryTimer = null;
    }
  }

  // Join chat room
  joinChat(chatId: string) {
    if (this.socket && this.socket.connected) {
      this.socket.emit('join-chat', chatId);
    }
  }

  // Leave chat room
  leaveChat(chatId: string) {
    if (this.socket && this.socket.connected) {
      this.socket.emit('leave-chat', chatId);
    }
  }

  // Send message
  sendMessage(chatId: string, message: {
    type: string;
    content: string;
    imageUrl?: string;
    location?: any;
  }) {
    if (this.socket && this.socket.connected) {
      this.socket.emit('send-message', { chatId, message });
    }
  }

  // Typing indicator
  setTyping(chatId: string, isTyping: boolean) {
    if (this.socket && this.socket.connected) {
      this.socket.emit('typing', { chatId, isTyping });
    }
  }

  // Mark messages as read
  markAsRead(chatId: string) {
    if (this.socket && this.socket.connected) {
      this.socket.emit('mark-read', chatId);
    }
  }

  // Check if WebSocket is connected
  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  // Observables for events
  get connected(): Observable<boolean> {
    return this.connected$.asObservable();
  }

  get onOrderStatusUpdate(): Observable<any> {
    return this.orderStatusUpdate$.asObservable();
  }

  get onOrderETAUpdate(): Observable<any> {
    return this.orderETAUpdate$.asObservable();
  }

  get onNewOrder(): Observable<any> {
    return this.newOrder$.asObservable();
  }

  get onChatMessage(): Observable<any> {
    return this.chatMessage$.asObservable();
  }

  get onNotification(): Observable<any> {
    return this.notification$.asObservable();
  }

  get onUserTyping(): Observable<any> {
    return this.userTyping$.asObservable();
  }

  get onMessagesRead(): Observable<any> {
    return this.messagesRead$.asObservable();
  }

  get onDeliveryAgentUpdate(): Observable<any> {
    return this.deliveryAgentUpdate$.asObservable();
  }

  get onDeliveryAgentLocation(): Observable<any> {
    return this.deliveryAgentLocation$.asObservable();
  }

  get onProductUpdate(): Observable<any> {
    return this.productUpdate$.asObservable();
  }

  get onVendorUpdate(): Observable<any> {
    return this.vendorUpdate$.asObservable();
  }

  get onOrderSettingsUpdate(): Observable<any> {
    return this.orderSettingsUpdate$.asObservable();
  }

  // CRITICAL: Observable for reconnect events
  get onReconnect(): Observable<void> {
    return this.reconnectTrigger$.asObservable();
  }

  ngOnDestroy() {
    this.authProfileSub?.unsubscribe();
    this.pendingDeliveryZoneId = null;
    this.disconnect();
    this.orderStatusUpdate$.complete();
    this.orderETAUpdate$.complete();
    this.newOrder$.complete();
    this.chatMessage$.complete();
    this.notification$.complete();
    this.userTyping$.complete();
    this.messagesRead$.complete();
    this.deliveryAgentUpdate$.complete();
    this.deliveryAgentLocation$.complete();
    this.productUpdate$.complete();
    this.vendorUpdate$.complete();
    this.orderSettingsUpdate$.complete();
    this.reconnectTrigger$.complete();
    this.connected$.complete();
  }
}

